import { SelectOptionsResolver } from '@ui/SelectField/types';
import BaseApiClient from '../BaseApiClient';

export type FILTER_NAMES =
  | 'risk_transfer'
  | 'payment_terms'
  | 'risk_transfer'
  | 'comingling'
  | 'termination_noticed_period';
interface DataListInterface {
  id: string;
  name: string;
  options: Array<{
    id: string;
    label: string;
    is_other: boolean;
    description: string | null;
  }>;
}

export interface ExpectedCloseDateChangeReasons {
  id: number;
  name: string;
  reason_type_id: number;
  reason_type: string;
}

export type TaskLostReason = ExpectedCloseDateChangeReasons;

interface ConfidenceScoreListInterface {
  id: number;
  name: string;
}

export class ApiDataLists extends BaseApiClient {
  async tasks(): Promise<DataListInterface[]> {
    return new Promise((resolve, reject) => {
      this.client
        .get('/data-lists?filter[group]=task')
        .then((res) => {
          if (res.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }
  async confidenceScores(): Promise<ConfidenceScoreListInterface[]> {
    return new Promise((resolve, reject) => {
      this.client
        .getList('/confidence-scores')
        .then((res) => {
          if (res.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async company(): Promise<DataListInterface[]> {
    return new Promise((resolve, reject) => {
      this.client
        .get('/data-lists?filter[group]=company')
        .then((res) => {
          if (res.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async product(): Promise<DataListInterface[]> {
    return new Promise((resolve, reject) => {
      this.client
        .get('/data-lists?filter[group]=product')
        .then((res) => {
          if (res.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async businessUnits(): Promise<
    (DataListInterface & { pipeline_count: number })[]
  > {
    return new Promise((resolve, reject) => {
      this.client
        .get('/business-units')
        .then((res) => resolve(res.data.data))
        .catch((err) => reject(err));
    });
  }

  businessUnitsResolver: SelectOptionsResolver = async () => {
    const items = await this.businessUnits();

    return items
      ? items.map((option) => ({
          value: `${option.id}`,
          label: option.name
        }))
      : null;
  };

  async expectedCloseDateChangeReasons(): Promise<
    Array<ExpectedCloseDateChangeReasons>
  > {
    return new Promise((resolve, reject) => {
      this.client
        .get('/expected-close-date-change-reasons')
        .then((res) => {
          if (res.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async taskLostReasons(): Promise<Array<TaskLostReason>> {
    return new Promise((resolve, reject) => {
      this.client
        .get('/task-lost-reasons')
        .then((res) => {
          const payload = res.data?.data ?? res.data;
          resolve(Array.isArray(payload) ? payload : []);
        })
        .catch((err) => reject(err));
    });
  }

  async filterName(filterName: FILTER_NAMES): Promise<DataListInterface[]> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/data-lists?filter[name]=${filterName}`)
        .then((res) => {
          if (res.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }
}

export { dataListsQueryKeys } from './query-keys';
export type { DataListsFilterName } from './query-keys';
export {
  useDataListsBusinessUnitsQuery,
  useDataListsCompanyQuery,
  useDataListsConfidenceScoresQuery,
  useDataListsExpectedCloseDateChangeReasonsQuery,
  useDataListsFilterNameQuery,
  useDataListsProductQuery,
  useDataListsTaskLostReasonsQuery,
  useDataListsTasksQuery
} from './use-data-lists-queries';

